import { useState } from "react";
import { base44 } from "@/api/base44Client";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";

export default function AdminImportCustomers() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState([]);

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setResult(null);
    setError("");
    setPreview([]);

    // Preview using extract
    setLoading(true);
    const { file_url } = await base44.integrations.Core.UploadFile({ file: f });
    const extracted = await base44.integrations.Core.ExtractDataFromUploadedFile({
      file_url,
      json_schema: {
        type: "object",
        properties: {
          customers: {
            type: "array",
            items: {
              type: "object",
              properties: {
                phone: { type: "string" }
              }
            }
          }
        }
      }
    });

    setLoading(false);
    if (extracted.status === "success") {
      const rows = Array.isArray(extracted.output) ? extracted.output : extracted.output?.customers || [];
      setPreview(rows.slice(0, 5));
      setFile({ ...f, _url: file_url, _rows: rows });
    } else {
      setError("تعذّر قراءة الملف. تأكد أن العمود باسم 'phone'");
    }
  };

  const handleImport = async () => {
    if (!file?._rows?.length) return;
    setLoading(true);
    setError("");
    let added = 0, skipped = 0, failed = 0;

    for (const row of file._rows) {
      const phone = String(row.phone || row["رقم الجوال"] || row["Phone"] || "").trim();
      if (!phone || phone.length < 9) { skipped++; continue; }

      // Normalize phone: ensure starts with 05
      const normalized = phone.startsWith("966") ? "0" + phone.slice(3) : phone;

      const existing = await base44.entities.Customer.filter({ phone: normalized });
      if (existing.length > 0) { skipped++; continue; }

      await base44.entities.Customer.create({
        phone: normalized,
        total_spins: 0,
        total_coupons_used: 0,
        total_coupons_unused: 0
      });
      added++;
    }

    setLoading(false);
    setResult({ added, skipped, failed, total: file._rows.length });
  };

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h1 className="text-2xl font-black text-foreground">📥 استيراد بيانات العملاء</h1>
            <p className="text-muted-foreground text-sm mt-1">ارفع ملف Excel أو CSV يحتوي على أرقام الجوال في عمود باسم <code className="bg-secondary px-1 rounded">phone</code> أو <code className="bg-secondary px-1 rounded">رقم الجوال</code></p>
          </div>

          {/* Upload Area */}
          <div className="border-2 border-dashed border-border rounded-2xl p-8 text-center space-y-4 bg-secondary/20">
            <div className="text-5xl">📊</div>
            <div>
              <p className="font-bold text-foreground">اختر ملف Excel أو CSV</p>
              <p className="text-xs text-muted-foreground mt-1">الصيغ المدعومة: xlsx, xls, csv</p>
            </div>
            <label className="inline-block cursor-pointer bg-primary text-white font-bold px-6 py-3 rounded-xl hover:bg-primary/90 transition-colors">
              اختر الملف
              <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFileChange} />
            </label>
            {file?.name && (
              <p className="text-sm text-muted-foreground">✅ {file.name}</p>
            )}
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-3 py-6">
              <div className="w-6 h-6 border-3 border-primary border-t-transparent rounded-full animate-spin"></div>
              <span className="text-muted-foreground">جاري المعالجة...</span>
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 text-destructive text-sm font-medium">
              ⚠️ {error}
            </div>
          )}

          {/* Preview */}
          {preview.length > 0 && !loading && (
            <div className="space-y-3">
              <h3 className="font-bold text-foreground">معاينة البيانات (أول 5 صفوف):</h3>
              <div className="bg-secondary/30 rounded-xl overflow-hidden border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-secondary">
                    <tr>
                      {Object.keys(preview[0]).map(k => (
                        <th key={k} className="px-4 py-2 text-right font-bold text-foreground">{k}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((row, i) => (
                      <tr key={i} className="border-t border-border">
                        {Object.values(row).map((v, j) => (
                          <td key={j} className="px-4 py-2 text-muted-foreground" dir="ltr">{String(v)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">إجمالي الصفوف: <strong>{file._rows?.length}</strong></p>

              <button
                onClick={handleImport}
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-primary to-accent text-white font-black rounded-xl shadow-md hover:shadow-lg transition-all text-lg disabled:opacity-50"
              >
                🚀 استيراد {file._rows?.length} عميل
              </button>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-6 space-y-3">
              <h3 className="font-black text-green-800 dark:text-green-300 text-lg">✅ اكتمل الاستيراد!</h3>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-green-100 dark:bg-green-900/30 rounded-xl p-3">
                  <div className="text-2xl font-black text-green-700 dark:text-green-300">{result.added}</div>
                  <div className="text-xs text-green-600 dark:text-green-400">تم إضافته</div>
                </div>
                <div className="bg-yellow-100 dark:bg-yellow-900/30 rounded-xl p-3">
                  <div className="text-2xl font-black text-yellow-700 dark:text-yellow-300">{result.skipped}</div>
                  <div className="text-xs text-yellow-600 dark:text-yellow-400">موجود مسبقاً</div>
                </div>
                <div className="bg-secondary rounded-xl p-3">
                  <div className="text-2xl font-black text-foreground">{result.total}</div>
                  <div className="text-xs text-muted-foreground">إجمالي</div>
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800/30 rounded-2xl p-5 space-y-2">
            <h4 className="font-bold text-blue-800 dark:text-blue-300">📋 تعليمات تحضير الملف:</h4>
            <ul className="text-sm text-blue-700 dark:text-blue-400 space-y-1 list-disc list-inside">
              <li>يجب أن يكون عمود الهاتف باسم <strong>phone</strong> أو <strong>رقم الجوال</strong></li>
              <li>الأرقام بصيغة <strong dir="ltr">05xxxxxxxx</strong> أو <strong dir="ltr">9665xxxxxxxx</strong></li>
              <li>الأرقام المكررة ستُتجاهل تلقائياً</li>
              <li>الملفات المدعومة: Excel (xlsx, xls) أو CSV</li>
            </ul>
          </div>
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}